/**
 * Labeling Context - State management for the Feedback Labeling feature
 *
 * Provides centralized state management for:
 * - Authentication status and user information
 * - Feedback items and current position
 * - Label data and submission state
 * - Loading and error states
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type {
  LabelingState,
  LabelingAction,
  User,
  FeedbackItem,
  AuthResponse,
  RecommendationSnapshot
} from '@/types/labeling';
import * as labelingApi from '@/utils/labelingApi';
import { fetchRecommendationById } from '@/utils/sanityApi';

// Initial state
const initialState: LabelingState = {
  isAuthenticated: false,
  user: null,
  token: null,
  feedback: [],
  fieldDefinitions: [],
  currentIndex: 0,
  labelData: {},
  currentRecommendation: null,
  sanityError: null,
  isLoading: false,
  error: null,
  successMessage: null
};

// Reducer function
const labelingReducer = (state: LabelingState, action: LabelingAction): LabelingState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_SUCCESS':
      return { ...state, successMessage: action.payload, error: null };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
        isLoading: false
      };

    case 'LOGOUT':
      return {
        ...initialState,
        feedback: [], // Clear feedback on logout
        fieldDefinitions: [],
        currentIndex: 0,
        labelData: {},
        currentRecommendation: null,
        sanityError: null
      };

    case 'SET_FEEDBACK':
      return {
        ...state,
        feedback: action.payload,
        currentIndex: action.payload.length > 0 ? 0 : -1,
        isLoading: false
      };

    case 'SET_FIELD_DEFINITIONS':
      return { ...state, fieldDefinitions: action.payload };

    case 'SET_CURRENT_RECOMMENDATION':
      return { ...state, currentRecommendation: action.payload, sanityError: null };

    case 'SET_SANITY_ERROR':
      return { ...state, sanityError: action.payload, currentRecommendation: null };

    case 'SET_CURRENT_INDEX':
      return { ...state, currentIndex: action.payload };

    case 'UPDATE_LABEL_DATA':
      return {
        ...state,
        labelData: action.payload
      };

    case 'CLEAR_LABEL_DATA':
      return { ...state, labelData: {} };

    case 'REMOVE_LABELED_ITEM':
      // Remove the labeled item from the feedback array
      const updatedFeedback = state.feedback.filter(
        item => item.feedbackId !== action.payload
      );
      // Adjust current index if needed
      let newIndex = state.currentIndex;
      if (state.currentIndex >= updatedFeedback.length && updatedFeedback.length > 0) {
        newIndex = updatedFeedback.length - 1;
      } else if (updatedFeedback.length === 0) {
        newIndex = -1;
      }
      return {
        ...state,
        feedback: updatedFeedback,
        currentIndex: newIndex,
        labelData: {}
      };

    default:
      return state;
  }
};

// Context type
interface LabelingContextType {
  state: LabelingState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadFeedback: () => Promise<void>;
  fetchCurrentRecommendation: () => Promise<void>;
  submitLabel: (labelData: { feedbackId: number; recommendationId?: string; fields: Record<string, any>; sanitySnapshot?: RecommendationSnapshot }) => Promise<void>;
  setCurrentIndex: (index: number) => void;
  updateLabelData: (data: Record<string, any>) => void;
  clearLabelData: () => void;
  nextFeedback: () => void;
  previousFeedback: () => void;
  skipFeedback: () => void;
  clearError: () => void;
  clearSuccess: () => void;
}

// Create context
const LabelingContext = createContext<LabelingContextType | undefined>(undefined);

// Token storage key
const TOKEN_STORAGE_KEY = 'labeling_token';
const USER_STORAGE_KEY = 'labeling_user';

// Provider component
export const LabelingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(labelingReducer, initialState);

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);

    if (storedToken && storedUser) {
      try {
        const user: User = JSON.parse(storedUser);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { token: storedToken, user }
        });
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    }
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await labelingApi.authenticate(email, password);

      // Store token and user in localStorage
      localStorage.setItem(TOKEN_STORAGE_KEY, response.token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.user));

      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    dispatch({ type: 'LOGOUT' });
  }, []);

  // Load feedback function
  const loadFeedback = useCallback(async () => {
    if (!state.token) {
      dispatch({ type: 'SET_ERROR', payload: 'Not authenticated' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await labelingApi.getUnlabeledFeedback(state.token);
      dispatch({ type: 'SET_FEEDBACK', payload: response.feedback });
      dispatch({ type: 'SET_FIELD_DEFINITIONS', payload: response.fieldDefinitions });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load feedback';

      // Handle token expiration
      if (errorMessage.includes('expired') || errorMessage.includes('session')) {
        logout();
      }

      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, [state.token, logout]);

  // Fetch current recommendation from Sanity
  const fetchCurrentRecommendation = useCallback(async () => {
    const current = state.feedback[state.currentIndex];
    if (!current) return;

    // Skip fetching if no recommendationId (general feedback)
    if (!current.recommendationId || current.recommendationId.trim() === '') {
      dispatch({ type: 'SET_CURRENT_RECOMMENDATION', payload: null });
      dispatch({ type: 'SET_SANITY_ERROR', payload: null });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const result = await fetchRecommendationById(current.recommendationId);

      if (result.error) {
        dispatch({ type: 'SET_SANITY_ERROR', payload: result.error });
      } else if (result.data) {
        dispatch({ type: 'SET_CURRENT_RECOMMENDATION', payload: result.data });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_SANITY_ERROR', payload: {
        type: 'network_error',
        message: error.message || 'Network error fetching recommendation'
      }});
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.feedback, state.currentIndex]);

  // Submit label function
  const submitLabel = useCallback(async (labelData: { feedbackId: number; recommendationId?: string; fields: Record<string, any>; sanitySnapshot?: RecommendationSnapshot }) => {
    if (!state.token) {
      dispatch({ type: 'SET_ERROR', payload: 'Not authenticated' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      await labelingApi.submitLabel(state.token, labelData);

      // Remove the labeled item from the list
      dispatch({ type: 'REMOVE_LABELED_ITEM', payload: labelData.feedbackId });

      dispatch({ type: 'SET_SUCCESS', payload: 'Label submitted successfully' });
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit label';

      // Handle token expiration
      if (errorMessage.includes('expired') || errorMessage.includes('session')) {
        logout();
      }

      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, [state.token, logout]);

  // Navigation functions
  const setCurrentIndex = useCallback((index: number) => {
    dispatch({ type: 'SET_CURRENT_INDEX', payload: index });
  }, []);

  const nextFeedback = useCallback(() => {
    if (state.currentIndex < state.feedback.length - 1) {
      dispatch({ type: 'SET_CURRENT_INDEX', payload: state.currentIndex + 1 });
      dispatch({ type: 'CLEAR_LABEL_DATA' });
    }
  }, [state.currentIndex, state.feedback.length]);

  const previousFeedback = useCallback(() => {
    if (state.currentIndex > 0) {
      dispatch({ type: 'SET_CURRENT_INDEX', payload: state.currentIndex - 1 });
      dispatch({ type: 'CLEAR_LABEL_DATA' });
    }
  }, [state.currentIndex]);

  const skipFeedback = useCallback(() => {
    // Skip just moves to next without submitting
    nextFeedback();
  }, [nextFeedback]);

  // Label data management
  const updateLabelData = useCallback((data: Record<string, any>) => {
    dispatch({ type: 'UPDATE_LABEL_DATA', payload: data });
  }, []);

  const clearLabelData = useCallback(() => {
    dispatch({ type: 'CLEAR_LABEL_DATA' });
  }, []);

  // Clear error and success messages
  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const clearSuccess = useCallback(() => {
    dispatch({ type: 'SET_SUCCESS', payload: null });
  }, []);

  const value: LabelingContextType = {
    state,
    login,
    logout,
    loadFeedback,
    fetchCurrentRecommendation,
    submitLabel,
    setCurrentIndex,
    updateLabelData,
    clearLabelData,
    nextFeedback,
    previousFeedback,
    skipFeedback,
    clearError,
    clearSuccess
  };

  return <LabelingContext.Provider value={value}>{children}</LabelingContext.Provider>;
};

// Custom hook to use the labeling context
export const useLabeling = (): LabelingContextType => {
  const context = useContext(LabelingContext);
  if (context === undefined) {
    throw new Error('useLabeling must be used within a LabelingProvider');
  }
  return context;
};
